import { Form, Input, DatePicker, Typography, Row, Col, Space, message, Segmented, Button, Flex, Divider } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Container from "../components/Container";
import LogoImage from "../components/LogoImage";
import ConfirmButton from "../components/ConfirmButton";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetUsimOrdersQuery, useUpdateActivationDateMutation } from "../api/apiSlice";
import styled from "styled-components";
import { UserActivationItem, UsimOrderItem } from "@/schemas/UsimOrdersResponse";
import { colors } from "../style";
import UsimProductCard from "../components/UsimProductCard";
import { loadLocalUsimData, saveLocalUsimData } from "../utils/localStorage";
import { CopyOutlined } from "@ant-design/icons";
const { Title, Text } = Typography;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 2px;
  max-width: 100%;
`;

const MobileLabel = styled(Text)`
  display: block;
  font-weight: bold;
  color: ${colors.primary};
  margin-bottom: 4px;
  @media (min-width: 576px) {
    display: none;
  }
`;

const DesktopHeaderRow = styled(Row)`
  display: none;
  @media (min-width: 576px) {
    display: flex;
  }
`;

const ProductRow = styled(Row)`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  @media (min-width: 576px) {
    margin-bottom: 8px;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export function ArrivalDateForm(props: {
  products: UsimOrderItem[];
  handleActivationDateChange: () => void;
  disabledPastDates: (current: Dayjs) => boolean;
  orderNo: string;
}) {
  const { products, disabledPastDates, orderNo } = props;
  const navigate = useNavigate();
  const [userActivations, setUserActivations] = useState<UserActivationItem[]>([]);
  const [updateActivationDate, { isLoading }] = useUpdateActivationDateMutation();
  const validProducts = products.filter((product) => {
    const isActivationRequired = product.hasActivation == true && product.status == '10';
    const isKycRequired = product.type?.toLowerCase().includes("australia") || product.type?.toLowerCase().includes("au");
    return isActivationRequired || isKycRequired;
  });
  useEffect(() => {
    const activations: UserActivationItem[] = validProducts.map((product) => (
      {
        shopId: product.shopId,
        activationDate: product.activationDate,
        orderNumber: orderNo
      }));
    setUserActivations(activations);
  }, [products, orderNo]);

  const isSubmitDisabled = userActivations.some((item, index) => {
    const product = validProducts[index];
    if (product?.hasActivation && product?.status == '10') {
      return !item.activationDate;
    }
    return false;
  });
  const handleDateChange = (date: Dayjs | null, index: number) => {
    const newActivations = [...userActivations];
    newActivations[index].activationDate = date ? date.format() : "";
    setUserActivations(newActivations);
  };

  const handleFormSubmit = async () => {
    try {
      let response: any = await updateActivationDate(userActivations).unwrap();
      if (response['errors'] && response['errors'].length > 0) {

        message.error(response['errors'][0].error);
      }
      else {
        message.success("Activation date(s) updated successfully");
      }
      // window.location.href = `/status/${orderNo}`;
    } catch (err) {

      message.error("Failed to update activation date(s)");
    }
  };

  return (
    <Form style={{ width: "100%" }} onFinish={handleFormSubmit}>
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 16,
          padding: 24,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <DesktopHeaderRow gutter={12}>
            <Col xs={24} sm={6}>
              <Space direction="vertical" size={0}>
                <Text strong style={{ color: colors.primary }}>Product</Text>
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={0}>
                <Text strong style={{ color: colors.primary }}>ICCID</Text>
              </Space>
            </Col>
            <Col xs={24} sm={6}>
              <Space direction="vertical" size={0}>
                <Text strong style={{ color: colors.primary }}>Arrival Date</Text>
              </Space>
            </Col>
            <Col xs={24} sm={4}>
              <Space direction="vertical" size={0}>
                <Text strong style={{ color: colors.primary }}>KYC Details</Text>
              </Space>
            </Col>
          </DesktopHeaderRow>
          {validProducts.length > 0 ? validProducts.map((product, index) => {
            const isActivationRequired = product.hasActivation == true && product.status == '10';
            const isKycRequired = product.type?.toLowerCase().includes("australia") || product.type?.toLowerCase().includes("au");
            return (
            <ProductRow key={index} gutter={[12, 12]} align="middle">
              <Col xs={24} sm={6}>
                <Space direction="vertical" size={0} style={{ width: "100%", textAlign: "start" }}>
                  <MobileLabel>Product</MobileLabel>
                  <Text strong>{product.mapping?.country ?? "-"}</Text>
                </Space>
              </Col>
              <Col xs={30} sm={8}>
                <Space direction="vertical" size={0} style={{ width: "100%", textAlign: "start" }}>
                  <MobileLabel>ICCID</MobileLabel>
                  <Text strong>{product.iccid || "-"}</Text>
                </Space>
              </Col>
              <Col xs={18} sm={6}>
                <Space direction="vertical" size={0} style={{ width: "100%", textAlign: "start" }}>
                  <MobileLabel>Arrival Date</MobileLabel>
                  {isActivationRequired || isKycRequired ? (
                    <DatePicker
                      size="large"
                      defaultValue={product.activationDate ? dayjs(product.activationDate) : undefined}
                      format="DD-MMM-YYYY"
                      style={{ width: "100%" }}
                      onClick={() =>{
                        if(product.status == "20")
                        {
                          message.success("Product is already activated. Please contact support")
                        }
                      }}
                      
                      placeholder="Select Activation Date"
                      onChange={(date) => handleDateChange(date, index)}
                      disabled={product.status == "20"}
                      disabledDate={disabledPastDates}
                    />
                  ) : (
                    <Text strong>{product.activationDate}</Text>
                  )}
                </Space>
              </Col>
              <Col xs={24} sm={4}>
                <Space direction="vertical" size={0} style={{ width: "100%", textAlign: "start" }}>
                  <MobileLabel>KYC Details</MobileLabel>
                  {isKycRequired ? (
                    <Button 
                      size="small" 
                      type="primary" 
                      onClick={() => window.open(`/ausreg?orderNumber=${encodeURIComponent(orderNo)}&id=${encodeURIComponent(product.shopId || "")}`, '_blank')}
                    >
                      Submit KYC
                    </Button>
                  ) : (
                    <Text>-</Text>
                  )}
                </Space>
              </Col>
            </ProductRow>
          )}) : (
              <div>
              This order doesn't require activation date or activation is already completed
              {/* {products.map((product) => (
                <div key={product.shopId}>
                  <Text strong>Product: </Text>
                  <Text>{product.type && product.type!.toLowerCase().includes("australia") ? "includes ausreg " : product.type}</Text>
                  <br />
                  <Text strong>ICCID: </Text>
                  <Text>{product.type || "N/A"}</Text>
                  <br />
                  <Text strong>Arrival Date: </Text>
                  <Text>{product.type || "N/A"}</Text>
                </div>
              ))} */}

              </div>
              
          )}

          <ConfirmButton
            htmlType="submit"
            disabled={isSubmitDisabled || isLoading}
          />
        </Space>
      </div>
    </Form>
  );
}